<template>
  <div class="gc-restricted-terms">
    <label class="gc-restricted-terms__label" :for="inputId">
      Do not translate
    </label>
    <v-combobox
      :id="inputId"
      :value="value"
      class="gc-restricted-terms__input"
      multiple
      chips
      small-chips
      deletable-chips
      dense
      outlined
      dark
      hide-details
      clearable
      persistent-placeholder
      :items="[]"
      :search-input.sync="search"
      :placeholder="placeholder"
      @input="onInput"
      @keydown="onKeydown"
    />
    <p class="gc-restricted-terms__hint">{{ hint }}</p>
    <div v-if="defaults.length" class="gc-restricted-terms__defaults">
      <span class="gc-restricted-terms__defaults-label">
        Built-in locked terms ({{ defaults.length }})
      </span>
      <div class="gc-restricted-terms__defaults-list">
        <span
          v-for="term in defaults"
          :key="term"
          class="gc-restricted-terms__default-chip"
          :class="{ 'gc-restricted-terms__default-chip--dup': isDuplicateDefault(term) }"
          :title="isDuplicateDefault(term) ? 'Already in your list' : 'Built-in keep-English term'"
        >
          {{ term }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = 'gc-translation-restricted-terms';
const MAX_TERMS = 40;
const MAX_TERM_CHARS = 80;

export { STORAGE_KEY, MAX_TERMS, MAX_TERM_CHARS };

export default {
  name: 'TranslationRestrictedTermsField',

  props: {
    value: {
      type: Array,
      default: () => []
    },
    defaults: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Add brand or feature name, press Enter'
    },
    hint: {
      type: String,
      default:
        'Kept English in titles and body. Built-in defaults (Free Spins, Wild, …) always apply.'
    }
  },

  data() {
    return {
      search: ''
    };
  },

  computed: {
    inputId() {
      return `gc-restricted-terms-${this._uid}`;
    }
  },

  methods: {
    normalizeList(list) {
      const seen = new Set();
      const out = [];
      for (const raw of list || []) {
        const text = String(raw ?? '')
          .trim()
          .replace(/\s+/g, ' ')
          .slice(0, MAX_TERM_CHARS);
        if (!text) continue;
        const key = text.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(text);
        if (out.length >= MAX_TERMS) break;
      }
      return out;
    },
    onInput(list) {
      const next = this.normalizeList(list);
      this.$emit('input', next);
      this.search = '';
    },
    onKeydown(event) {
      if (event.key !== ',' || !this.search) return;
      event.preventDefault();
      const parts = String(this.search)
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);
      if (!parts.length) return;
      this.onInput([...(this.value || []), ...parts]);
    },
    isDuplicateDefault(term) {
      const key = String(term || '')
        .trim()
        .toLowerCase();
      return (this.value || []).some(entry => String(entry).trim().toLowerCase() === key);
    }
  }
};

export function loadStoredRestrictedTerms() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    const out = [];
    for (const rawTerm of parsed) {
      const text = String(rawTerm ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, MAX_TERM_CHARS);
      if (!text) continue;
      const key = text.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(text);
      if (out.length >= MAX_TERMS) break;
    }
    return out;
  } catch {
    return [];
  }
}

export function saveStoredRestrictedTerms(terms) {
  try {
    const list = Array.isArray(terms) ? terms : [];
    const seen = new Set();
    const out = [];
    for (const raw of list) {
      const text = String(raw ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, MAX_TERM_CHARS);
      if (!text) continue;
      const key = text.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(text);
      if (out.length >= MAX_TERMS) break;
    }
    if (out.length) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(out));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
</script>
